import { useState, useEffect, useCallback } from 'react';
import { groupItemsById, compareItems } from '../utils/itemUtils';

const useItemManagement = (initialItems) => {
  const [initialItemState, setInitialItemState] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [changes, setChanges] = useState([]); // Track changes separately

  const initializeState = useCallback((items) => {
    const groupedInitialItems = groupItemsById(items || []);
    setInitialItemState(groupedInitialItems);
    setSelectedItems(groupedInitialItems);
    setChanges([]);
  }, []);

  useEffect(() => {
    initializeState(initialItems);
  }, [initialItems, initializeState]);

  const handleItemClick = (item) => {
    setSelectedItems((prevItems) => {
      const groupedItems = groupItemsById([...prevItems, item]);
      trackChanges(groupedItems);
      return groupedItems;
    });
  };

  const handleQuantityChange = (itemId, delta) => {
    setSelectedItems((prevItems) => {
      const updatedItems = prevItems.reduce((acc, item) => {
        if (item.lineKey === itemId) {
          const newQuantity = item.quantity + delta;
          if (newQuantity > 0) {
            acc.push({ ...item, quantity: newQuantity });
          }
        } else {
          acc.push(item);
        }
        return acc;
      }, []);
      trackChanges(updatedItems);
      return updatedItems;
    });
  };

  const handleItemNoteChange = (itemId, note) => {
    setSelectedItems((prevItems) => {
      const updatedItems = prevItems.map((item) =>
        item.lineKey === itemId ? { ...item, notes: note } : item
      );
      const regroupedItems = groupItemsById(updatedItems);
      trackChanges(regroupedItems);
      return regroupedItems;
    });
  };

  const handleDeleteItem = (itemId) => {
    setSelectedItems((prevItems) => {
      const updatedItems = prevItems.filter((item) => item.lineKey !== itemId);
      trackChanges(updatedItems);
      return updatedItems;
    });
  };

  const trackChanges = (updatedItems) => {
    const changes = updatedItems.reduce((acc, item) => {
      const originalItem = initialItemState.find((i) => i.lineKey === item.lineKey);
      if (!originalItem) {
        // New item
        acc.push({ ...item, changeType: 'add' });
      } else if (originalItem.quantity !== item.quantity) {
        // Updated quantity
        const quantityChange = item.quantity - originalItem.quantity;
        acc.push({ ...item, changeType: quantityChange > 0 ? 'add' : 'remove', quantityChange });
      }
      return acc;
    }, []);

    // Check for removed items
    initialItemState.forEach((origItem) => {
      if (!updatedItems.some((item) => item.lineKey === origItem.lineKey)) {
        changes.push({ ...origItem, changeType: 'remove', quantityChange: -origItem.quantity });
      }
    });

    setChanges(changes);
  };

  const hasChanges = compareItems(initialItemState, selectedItems);
  const resetToInitialState = useCallback(() => {
    initializeState(initialItems);
  }, [initialItems, initializeState]);

  return {
    selectedItems,
    handleItemClick,
    handleQuantityChange,
    handleItemNoteChange,
    handleDeleteItem,
    hasChanges,
    changes,
    resetToInitialState,
  };
};

export default useItemManagement;
